import type { ComponentType } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

type MetricCardProps = {
  label: string;
  value: string;
  /** Small supporting line under the label. May contain HTML entities. */
  subLabel?: string;
  icon?: ComponentType<{ className?: string }>;
  accent?: "default" | "attention";
  /** Renders the whole card as a link. */
  href?: string;
  className?: string;
};

export function MetricCard({
  label,
  value,
  subLabel,
  icon: Icon,
  accent = "default",
  href,
  className,
}: MetricCardProps) {
  const card = (
    <Card
      className={cn(
        "h-full gap-0 overflow-hidden py-0 transition-colors",
        accent === "attention"
          ? "border-warning/35 bg-warning/12 text-warning-foreground"
          : href && "hover:border-border hover:bg-accent/40",
        className
      )}
    >
      <CardContent className="flex h-full flex-col gap-4 p-4">
        {Icon ? (
          <div className="icon-tile">
            <Icon className="size-5" />
          </div>
        ) : null}
        <div className="mt-auto min-w-0 space-y-1">
          <p
            className="truncate text-2xl font-bold leading-none tracking-tight tabular-nums sm:text-3xl"
            title={value}
          >
            {value}
          </p>
          <p
            className="truncate text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground"
            title={label}
          >
            {label}
          </p>
          {subLabel ? (
            <p
              className="line-clamp-2 text-xs leading-snug text-muted-foreground"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: internal metric copy only
              dangerouslySetInnerHTML={{ __html: subLabel }}
            />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );

  if (href) {
    return (
      <Link
        to={href}
        className="block h-full rounded-lg focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/45 focus-visible:outline-none"
      >
        {card}
      </Link>
    );
  }

  return card;
}
